/**
 * Contratos da Curadoria (Onda 6 / A11) — espelhos dos DTOs de `originei-oportunidades`
 * (`CuradoriaFilaResponse`, `CuradoriaDecisaoRequest`, `ReservasResumoResponse`). Campo a
 * campo, na mesma ordem.
 */
import type { EtapaComercial, StatusReserva } from './statusReserva'
import type { ResultadoElegibilidade, RotaOperacao } from './elegibilidade'

/**
 * Espelha `entity/StatusCuradoria` — DERIVADO por requisição, nunca persistido:
 *  - DUPLICIDADE_POSSIVEL: outra reserva não perdida com o mesmo CNPJ (`conflitosCnpj`);
 *  - ELEGIVEL: sem duplicidade e checklist obrigatório completo;
 *  - EM_ANALISE: o restante (há o que revisar).
 */
export type StatusCuradoria = 'RASCUNHO' | 'DUPLICIDADE_POSSIVEL' | 'ELEGIVEL' | 'EM_ANALISE'

export const STATUS_CURADORIA_LABEL: Record<StatusCuradoria, string> = {
  /** 09/09: ainda não enviada — listada para acompanhamento, sem decisão. */
  RASCUNHO: 'Rascunho',
  DUPLICIDADE_POSSIVEL: 'Duplicidade possível',
  ELEGIVEL: 'Elegível',
  EM_ANALISE: 'Em análise',
}

export const STATUS_CURADORIA_COR: Record<StatusCuradoria, 'warning' | 'success' | 'info' | 'default'> = {
  RASCUNHO: 'default',
  DUPLICIDADE_POSSIVEL: 'warning',
  ELEGIVEL: 'success',
  EM_ANALISE: 'info',
}

/** Espelha `CuradoriaFilaResponse.Resumo`. */
export interface CuradoriaResumo {
  /** ENVIADA — chegaram e ninguém abriu. */
  novas: number
  /** EM_ANALISE_ORIGINEI + COM_PENDENCIA. */
  emCuradoria: number
  /** APROVADA_ORIGINEI com última movimentação na janela. */
  liberadas: number
  janelaLiberadasDias: number
  /** Solicitações de renovação em aberto (SOLICITADA/EM_ANALISE). */
  excecoes: number
  /** RASCUNHO no escopo (09/09): ainda não enviadas, listadas para acompanhamento. */
  rascunhos: number
  /**
   * P2 (AN-PP2 §5.3, Q67): reservas não terminais com rota GRANDES_OPERACOES — `count` da MESMA
   * query de `GET /fila/grandes-operacoes`. Opcional só para conviver com o backend anterior.
   */
  grandesOperacoes?: number | null
}

/** Espelha `CuradoriaFilaResponse.Reserva`. */
export interface CuradoriaReserva {
  id: string
  numero: number | null
  cnpj: string | null
  razaoSocial: string | null
  nomeFantasia: string | null
  tipoNecessidade: string | null
  produtoId: string | null
  valorEstimado: number | null
  status: StatusReserva
  etapaComercial: EtapaComercial
  statusCuradoria: StatusCuradoria
  canal: string | null
  usuarioResponsavel: string | null
  /** ISO YYYY-MM-DD. */
  prazoReserva: string | null
  documentosObrigatoriosFaltantes: number
  documentosObrigatoriosTotal: number
  /** 0–100 (100 quando o produto não exige nada). */
  percentualChecklist: number
  /** Ids das outras reservas (no escopo de quem pede) com o mesmo CNPJ. */
  conflitosCnpj: string[]
  /** Conflitos que existem mas ficam fora do escopo — contados, nunca listados. */
  conflitosForaDoEscopo: number
  /** P2 (AN-PP2 §5.3, Q67): rota gravada da reserva (só `RECEIVABLES_RANGE`); omitida quando nula. */
  rotaOperacao?: RotaOperacao | null
  /** P2 (AN-PP2 §6): resultado da ÚLTIMA avaliação gravada; omitido = reserva sem snapshot. */
  resultadoElegibilidade?: ResultadoElegibilidade | null
  /**
   * P2 (AN-PP2 §6): a última avaliação impede o Encaminhamento (chip "Alerta de elegibilidade").
   * Decidido no servidor (`CuradoriaFilaResponse.alertaElegibilidade`); na Curadoria é só alerta (Q66).
   */
  alertaElegibilidade: boolean
  createdAt: string
  updatedAt: string
  /**
   * P3 (AN-PP3 §6.2): itens do checklist (obrigatórios, opcionais e solicitações) em RECEBIDO —
   * esperando o Operador validar ou recusar. Opcional só para conviver com o backend anterior.
   */
  itensAguardandoValidacao?: number | null
  /** P3: itens do checklist em RECUSADO. */
  itensRecusados?: number | null
  /**
   * Nome do consultor responsável (`usuarioResponsavel`) pela projeção local de usuários do
   * backend (consulta em lote); nulo sem projeção. A tela só mostra na visão ADMIN. Opcional só
   * para conviver com o backend anterior.
   */
  consultorNome?: string | null
}

/** Filtro de servidor da fila (`?checklist=`) — P3: só linhas com item RECEBIDO esperando o Operador. */
export type FiltroChecklistFila = 'AGUARDANDO_VALIDACAO'

/** Espelha `CuradoriaFilaResponse`. */
export interface CuradoriaFila {
  resumo: CuradoriaResumo
  reservas: CuradoriaReserva[]
}

export type DecisaoCuradoria = 'APROVAR' | 'SOLICITAR_COMPLEMENTO' | 'REPROVAR'

export const DECISAO_CURADORIA_LABEL: Record<DecisaoCuradoria, string> = {
  APROVAR: 'Liberar para estruturação',
  SOLICITAR_COMPLEMENTO: 'Solicitar complemento',
  REPROVAR: 'Reprovar',
}

/** Espelha `CuradoriaDecisaoRequest` — motivo SEMPRE obrigatório. */
export interface CuradoriaDecisaoRequest {
  decisao: DecisaoCuradoria
  motivo: string
  /** Só em SOLICITAR_COMPLEMENTO: vira pendência para o CONSULTOR. */
  pendencia?: {
    descricao?: string | null
    documentoSolicitado?: string | null
    /** ISO YYYY-MM-DD. */
    prazoResposta?: string | null
  } | null
}

/**
 * Espelha `ReservasResumoResponse` (`GET /v1/oportunidades/resumo`): KPIs de Minhas
 * Reservas pela regra ÚNICA do backend — a tela não conta mais a lista.
 */
export interface ReservasResumo {
  /** Etapa comercial fora de GANHO/PERDIDO. */
  ativas: number
  /** Ativas com prazo dentro da janela automática de renovação (1–5 dias). */
  aVencer: number
  emEstruturacao: number
  ganhas: number
}
